<template>
    <div :class="[$style.container, { [$style.fullscreen]: isFullscreen }]">
        <div :class="$style.content">
            <template v-if="game">
                <template v-if="isGameStarted">
                    <iframe
                        :ref="iframeOnMount"
                        :src="exportGameURL"
                        :class="$style.iframe"
                        :title="game.title"
                        allow="fullscreen"
                    />
                </template>
                <template v-else>
                    <div
                        :class="$style.background"
                        :style="{ backgroundImage: `url(${game.preview})` }"
                    />
                    <div :class="$style.cover">
                        <img
                            :class="$style.preview"
                            :src="game.preview"
                            :alt="game.title"
                        />
                        <div :class="$style.cover_buttons">
                            <template v-if="limitedAvailablity">
                                <Typography
                                    type="paragraph"
                                    size="m"
                                    accent
                                    :class="$style.limited_availability"
                                >
                                    {{ limitedAvailablity }}
                                </Typography>
                            </template>
                            <template v-else-if="isUserInfoLoaded && !isAuthorized">
                                <Button
                                    size="l"
                                    width="full"
                                    effect="bevel"
                                    color="golden"
                                    interfaceTarget="play"
                                    @click="handlePlay"
                                >
                                    {{ $tr('gameContainer.playNoAuth') }}
                                </Button>
                            </template>
                            <template v-else>
                                <Button
                                    size="l"
                                    width="full"
                                    effect="bevel"
                                    color="golden"
                                    interfaceTarget="play"
                                    @click="handlePlay"
                                >
                                    {{ $tr('gameContainer.play') }}
                                </Button>
                            </template>
                        </div>
                    </div>
                </template>
            </template>
        </div>
        <div :class="$style.footer">
            <template v-if="props.game">
                <Typography
                    is="div"
                    type="label"
                    size="s"
                    :accent="true"
                    :class="$style.title"
                >
                    {{ props.game.title }}
                </Typography>
                <div :class="$style.pannel_spacer" />
                <Button
                    :class="$style.report_button_wide"
                    visual="text"
                    color="carbon-400"
                    iconLeft="report"
                    interfaceTarget="report"
                    @click="handleReport"
                >
                    {{ $tr('gameContainer.report') }}
                </Button>
                <Button
                    :class="$style.report_button_short"
                    visual="text"
                    color="carbon-400"
                    icon="report"
                    interfaceTarget="report"
                    @click="handleReport"
                />
                <div style="position: relative">
                    <Tooltip
                        :content="$tr('gameContainer.link_copied')"
                        :shown="showCopied"
                    >
                        <Button
                            visual="text"
                            color="carbon-400"
                            iconLeft="share"
                            interfaceTarget="share-game"
                            @click="handleShare"
                        >
                            {{ $tr('gameContainer.share') }}
                        </Button>
                    </Tooltip>
                </div>
                <template v-if="isDesktop">
                    <Button
                        v-if="!isFullscreen"
                        visual="text"
                        color="carbon-400"
                        iconLeft="fullscreen"
                        interfaceTarget="fullscreen"
                        @click="toggleFullscreen()"
                        @pointerdown="preventGameLooseFocus"
                    >
                        {{ $tr('gameContainer.fullscreen') }}
                    </Button>
                    <Button
                        v-else
                        visual="text"
                        color="carbon-400"
                        iconLeft="collapse"
                        interfaceTarget="fullscreen"
                        @click="toggleFullscreen()"
                        @pointerdown="preventGameLooseFocus"
                    >
                        {{ $tr('gameContainer.fullscreen') }}
                    </Button>
                </template>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import Button from '@/components_new/Button.vue'
import type { APIGameType, GameCategoriesInfoType, PlatformType } from '@/types'
import { useUserStore } from '@/store/user-store/user-store'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import Typography from '@/components_new/Typography.vue'
import Tooltip from '@/components_new/Tooltip.vue'
import { useLoggerStore } from '@/store/logger'
import { ROUTE_NAMES } from '@/router/constants'
import { initExportApi, DestroyExportApiFunction } from '@/modules/export-api'
import { useTr } from '@/composables/use-tr'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { useConfig } from '@/composables/config'

const tr = useTr()
const { VUE_APP_HOST_URL } = useConfig()

const router = useRouter()
const route = useRoute()
const getExternalUrl = useGetExternalUrl()

const userStore = useUserStore()
const { platform, query } = useAppStore()
const loggerStore = useLoggerStore()

const { isAuthorized, isUserInfoLoaded } = storeToRefs(userStore)

interface GameDetailsModalProps {
    game?: APIGameType
    categories?: GameCategoriesInfoType
    isGameStarted: boolean
}

const props = defineProps<GameDetailsModalProps>()

const exportGameURL = computed(() => {
    if (!props.game) {
        return ''
    }

    return getExternalUrl({
        name: ROUTE_NAMES.EXPORT_GAME_PAGE,
        params: {
            locale: '', // always load export page without locale
            game: props.game.hru,
        },
        // enable portal API
        query: { ...route.query, portalApi: 1 },
    })
})

const availablePlatforms = computed(() => {
    const result: PlatformType[] = []
    if (props.game?.is_desktop) {
        result.push('desktop')
    }
    if (props.game?.is_android) {
        result.push('android')
    }
    if (props.game?.is_ios) {
        result.push('ios')
    }

    return result
})

const platformTranslations = computed(() => ({
    desktop: tr('gameContainer.desktop'),
    android: tr('gameContainer.android'),
    ios: tr('gameContainer.ios'),
}))

const limitedAvailablity = computed(() => {
    if (isPlatformAvailable.value || !props.game) {
        return false
    }

    const translations = platformTranslations.value

    const firstPlatform = availablePlatforms.value[0]

    if (availablePlatforms.value.length === 1) {
        return tr('gameContainer.limitedAvailablity1', {
            platform: translations[firstPlatform],
        })
    }

    const restPlatforms = availablePlatforms.value.slice(0, -1)
    const lastPlatform = availablePlatforms.value.slice(-1)[0]

    return tr('gameContainer.limitedAvailablityMany', {
        platfroms: restPlatforms.map((p) => translations[p]).join(', '),
        lastPlatform: translations[lastPlatform],
    })
})

const isFullscreen = ref<boolean>(false)

const layoutStore = useLayoutStore()
const showCopied = ref(false)
const exportApiDestroy = ref<DestroyExportApiFunction>()

watch(
    () => isFullscreen.value,
    (value) => {
        layoutStore.setFullscreen(value)
    },
)

const iframeOnMount = (el: unknown | null) => {
    if (el) {
        initExportApi(el as HTMLIFrameElement, {
            authorizeUser: userStore.authorizeUser,
            getUser: userStore.getUser,
            chargeUser: userStore.chargeUser,
            closeGame: () => {
                stopGame()
                toggleFullscreen()
            },
        }).then((destroy: DestroyExportApiFunction) => {
            exportApiDestroy.value = destroy
        })
    } else {
        exportApiDestroy.value?.()
    }
}

function preventGameLooseFocus(event: PointerEvent) {
    event.preventDefault()
}

async function toggleFullscreen(forceFullscreen?: boolean) {
    if (typeof forceFullscreen === 'boolean') {
        isFullscreen.value = forceFullscreen
    } else {
        isFullscreen.value = !isFullscreen.value
    }
    if (document.fullscreenEnabled && isDesktop.value) {
        if (isFullscreen.value) {
            try {
                await document.body.requestFullscreen()
            } catch {
                // iOS or non-gesture call fullscreen
            }
        } else {
            try {
                await document.exitFullscreen()
            } catch {
                // we might be not in fullscreen mode
            }
        }
    }
}

const emit = defineEmits<{ startPlayingGame: []; openReportModal: []; stopPlayingGame: [] }>()

const isPlatformAvailable = computed(() => availablePlatforms.value.includes(platform))

const hasAutoplay = computed(
    // default autoplay ruins lighthouse perf on desktop
    // temporary disabling it, until better solutions
    () => query?.autoplay /* || isTabletPlus.value */ && isPlatformAvailable.value && !props.game?.is_hidden,
)

const isDesktop = computed(() => platform === 'desktop')

function startGame() {
    if (props.game?.is_hidden) {
        if (props.categories?.pathCategories[0]) {
            router.push(props.categories.pathCategories[0].href)
        }
        return
    }
    if (props.game && !props.isGameStarted) {
        if (!isDesktop.value) {
            toggleFullscreen(true)
        }
        emit('startPlayingGame')
    }
}

function stopGame() {
    if (!props.isGameStarted) {
        return
    }
    emit('stopPlayingGame')
}

function handlePlay() {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'play',
        action: 'click',
    })
    startGame()
}

function handleReport() {
    emit('openReportModal')
}

function handleShare() {
    if (!props.game) {
        return
    }
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'share',
        action: 'click',
    })
    const data = {
        title: props.game?.title,
        text: props.game?.description,
        url:
            VUE_APP_HOST_URL +
            getExternalUrl({
                name: ROUTE_NAMES.GAME_PAGE,
                params: {
                    game: props.game.hru,
                },
                query: { utm_source: 'share-game' },
            }),
    }
    if (!isDesktop.value && navigator.canShare && navigator.canShare(data)) {
        navigator.share(data)
    } else {
        navigator.clipboard.writeText(data.url).then(
            () => {
                showCopied.value = true
                setTimeout(() => {
                    showCopied.value = false
                }, 3000)
            },
            () => {
                console.error('Failed to copy text')
            },
        )
    }
}

const onFullscreenChange = () => {
    isFullscreen.value = document.fullscreenElement !== null
}

const onVisibilityChange = () => {
    if (!document.hidden) {
        startGame()
    }
}

onMounted(() => {
    if (document.fullscreenEnabled) {
        document.addEventListener('fullscreenchange', onFullscreenChange)
    }
    if (hasAutoplay.value) {
        onVisibilityChange()
        document.addEventListener('visibilitychange', onVisibilityChange)
    }
})

watch(
    () => props.game,
    () => {
        if (hasAutoplay.value) {
            onVisibilityChange()
        }
    },
)

onUnmounted(() => {
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    layoutStore.setFullscreen(false)
    exportApiDestroy.value?.()
})
</script>

<style module>
.container {
    background: #000;
    --container-border-radius: 16px;
    display: flex;
    flex-direction: column;
    position: relative;
}

.content {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    border-radius: var(--container-border-radius) var(--container-border-radius) 0 0;
}

.content > * {
    grid-column: 1;
    grid-row: 1;
    width: 100%;
    height: 100%;
    position: relative;
}

.background {
    background-size: cover;
    background-position: center;
    transform: scale(1.5);
    filter: blur(30px) brightness(0.4);
    pointer-events: none;
}

.cover {
    width: min-content;
    justify-self: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    gap: 24px;
}

.preview {
    width: 230px;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    object-fit: cover;
    border-radius: 12px;
    overflow: hidden;
}

.cover_buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.limited_availability {
    text-align: center;
    width: 100%;
}

.iframe {
    object-fit: contain;
}

.footer {
    background-color: var(--carbon-300);
    padding: 12px 16px;
    height: calc(32px + 12px * 2);
    box-sizing: border-box;
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    border-radius: 0 0 var(--container-border-radius) var(--container-border-radius);
}

.title {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pannel_spacer {
    flex-grow: 1;
}

.report_button_short {
    display: block;
}
.report_button_wide {
    display: none;
}

.fullscreen {
    --container-border-radius: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 100dvh;
    z-index: 1;
}

.fullscreen * {
    touch-action: manipulation;
}

.fullscreen .content {
    flex-grow: 1;
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
}
@media (--mobile) {
    .container {
        --container-border-radius: 0;
        margin: calc(var(--global-page-top-padding) * -1) calc(var(--global-padding-horizontal) * -1) 0;
    }

    .container.fullscreen {
        margin: 0;
    }

    .content {
        height: 344px;
    }

    .cover_buttons {
        flex-direction: column;
        gap: 12px;
    }

    .fullscreen .footer {
        display: none;
    }
}

@media (--tablet-plus) {
    .content {
        aspect-ratio: 16 / 9;
        min-height: 464px;
        height: auto;
    }
}

@media (--desktop-small-plus) {
    .preview {
        width: 420px;
        aspect-ratio: var(--thumbnail-aspect-ratio);
    }

    .cover_buttons {
        flex-direction: row;
        gap: 16px;
    }

    .report_button_short {
        display: none;
    }
    .report_button_wide {
        display: block;
    }
}
</style>
